/**
 * ESQUECI MINHA SENHA — as regras puras, sem banco e sem rede.
 *
 * POR QUE NÃO É SÓ "MANDAR O E-MAIL": neste produto o login é um e-mail só
 * na forma. O cadastro de equipe tem um modo INTERNO (`createTeamMember` com
 * `mode: "internal"`), feito para quem não tem e-mail de trabalho: o
 * administrador digita um login qualquer e o sistema devolve a senha gerada
 * uma vez. Em 23/10/2026, na Alliance, 18 dos 24 logins eram `nome@alliance`
 * — um domínio que não existe. Um fluxo que só manda e-mail deixaria três de
 * cada quatro pessoas na mesma tela de antes.
 *
 * Então são DOIS caminhos, escolhidos pela FORMA do que a pessoa digitou:
 *
 *  - e-mail de verdade (domínio com ponto e TLD plausível) → link de
 *    redefinição no e-mail;
 *  - qualquer outra coisa (`nome@alliance`, `marcelo@interno.local`) → o
 *    pedido vai para os administradores da construtora pelo sino, e quem
 *    redefine é o administrador, na janela "Definir senha" de Configurações ›
 *    Equipe e permissões › Usuários, que já existia.
 *
 * A ESCOLHA SAI DO QUE FOI DIGITADO, nunca do que existe no banco: a tela diz
 * a mesma coisa para um login que existe e para um que não existe, então não
 * dá para descobrir quem tem conta testando e-mails (enumeração). Quem lê a
 * tela entende o caminho; quem está sondando não aprende nada.
 */

/**
 * Domínios que PARECEM e-mail mas não entregam nada. `.local` e `.internal`
 * são reservados para rede interna, `.invalid` e `.test` para exemplo, e
 * `.localhost` para a própria máquina (RFC 2606 e 6761). O cadastro interno
 * sugere `@interno.local`, então este é o caso comum, não um detalhe.
 */
export const UNDELIVERABLE_TLDS = [
  "local",
  "localhost",
  "internal",
  "invalid",
  "test",
  "example",
  "lan",
  "home",
  "corp",
] as const;

/** Tira espaço, caixa alta e o que o teclado do celular costuma colar. */
export function normalizeLogin(raw: string): string {
  return raw.trim().replace(/\s+/g, "").toLowerCase();
}

/**
 * Este login é um e-mail que um servidor conseguiria entregar?
 *
 * Não é validação de e-mail: é a pergunta "vale a pena tentar mandar?". Um
 * `nome@alliance` passa na validação do navegador (o HTML não exige ponto no
 * domínio) e é justamente o caso que não entrega.
 */
export function isDeliverableEmail(login: string): boolean {
  const value = normalizeLogin(login);
  const at = value.indexOf("@");
  if (at <= 0 || value.indexOf("@", at + 1) !== -1) return false;

  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (!local || !domain) return false;
  if (/[^a-z0-9.!#$%&'*+/=?^_`{|}~-]/.test(local)) return false;
  if (!/^[a-z0-9.-]+$/.test(domain)) return false;
  if (domain.startsWith("-") || domain.startsWith(".")) return false;

  const labels = domain.split(".");
  if (labels.length < 2) return false;
  if (labels.some((l) => l.length === 0)) return false;

  const tld = labels[labels.length - 1];
  if (!/^[a-z]{2,}$/.test(tld)) return false;
  if ((UNDELIVERABLE_TLDS as readonly string[]).includes(tld)) return false;

  return true;
}

/**
 * O caminho da redefinição: `email` manda o link, `interno` pede ao
 * administrador. Derivado SÓ do que foi digitado — ver a nota do topo.
 */
export type ResetPath = "email" | "interno";

export function resetPathFor(login: string): ResetPath {
  return isDeliverableEmail(login) ? "email" : "interno";
}

/** Onde a pessoa escolhe a senha nova depois de abrir o link. */
export const NEW_PASSWORD_PATH = "/auth/nova-senha";

/**
 * O link que vai no e-mail. Montado por nós a partir do `hashed_token` que o
 * `generateLink` devolve, e não o `action_link` do Supabase: assim o link
 * aponta para o domínio da construtora (o produto é white-label e cada uma
 * entra pelo seu) e cai no `/auth/callback`, que já troca o token por sessão.
 */
export function recoveryUrl(origin: string, hashedToken: string): string {
  const base = origin.replace(/\/+$/, "");
  const params = new URLSearchParams({
    token_hash: hashedToken,
    type: "recovery",
    next: NEW_PASSWORD_PATH,
  });
  return `${base}/auth/callback?${params.toString()}`;
}

/**
 * A senha nova serve?
 *
 * 8 caracteres, a MESMA régua de Minha conta › Segurança
 * (`ChangePasswordSection`), porque aqui também é a PESSOA que escolhe. Os 6
 * da janela "Definir senha" do administrador são de outra situação: lá a
 * senha é provisória e combinada por um canal seguro.
 */
export const NEW_PASSWORD_MIN = 8;

export function newPasswordError(value: string, confirm: string): string | null {
  if (value.length === 0) return "Digite a senha nova.";
  if (value.length < NEW_PASSWORD_MIN)
    return `A senha precisa ter pelo menos ${NEW_PASSWORD_MIN} caracteres.`;
  if (confirm.length === 0) return "Repita a senha nova.";
  if (value !== confirm) return "As duas senhas não são iguais.";
  return null;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * O e-mail da redefinição, em texto e em HTML. Sem imagem e sem cor da marca:
 * é um e-mail de segurança, e o que importa é o link e o prazo. O nome da
 * construtora aparece porque quem recebe conhece ela, não a Centtra.
 */
export function passwordResetEmail(args: {
  tenantName: string;
  fullName: string | null;
  link: string;
}): { subject: string; html: string; text: string } {
  const empresa = args.tenantName || "Obra Centtra";
  const nome = (args.fullName ?? "").trim().split(/\s+/)[0] || null;
  const saudacao = nome ? `Olá, ${nome}.` : "Olá.";
  const subject = `Redefinir sua senha · ${empresa}`;

  const text = [
    saudacao,
    "",
    `Você pediu para redefinir a senha do ${empresa}. Abra o link abaixo e escolha a senha nova:`,
    "",
    args.link,
    "",
    "O link vale por 1 hora e só pode ser usado uma vez.",
    "Se não foi você que pediu, ignore este e-mail: sua senha continua a mesma.",
  ].join("\n");

  const html = [
    '<div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.6;color:#17181a">',
    `<p>${escapeHtml(saudacao)}</p>`,
    `<p>Você pediu para redefinir a senha do <strong>${escapeHtml(empresa)}</strong>. Abra o link abaixo e escolha a senha nova:</p>`,
    `<p><a href="${escapeHtml(args.link)}" style="font-weight:bold">Escolher uma senha nova</a></p>`,
    "<p>O link vale por 1 hora e só pode ser usado uma vez.</p>",
    "<p>Se não foi você que pediu, ignore este e-mail: sua senha continua a mesma.</p>",
    "</div>",
  ].join("");

  return { subject, html, text };
}
